import { useState, useEffect } from 'react';
import { Bot, Sliders, MessageCircle, Clock, ShieldCheck, FileText, Plus, Trash2, ChevronDown, ChevronUp, Sparkles, Globe, X } from 'lucide-react';

// ── Toggle ───────────────────────────────────────────────────────────
const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
    <button onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-hig-accent/30 ${on ? 'bg-hig-accent' : 'bg-hig-bg-tertiary'}`}
        role="switch" aria-checked={on}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
);

// ── Built-in instruction templates ──────────────────────────────────
interface InstructionTemplate {
    id: string;
    name: string;
    icon: string;
    category: string;
    shortDescription: string;
    prompt: string;
}

const BUILT_IN_TEMPLATES: InstructionTemplate[] = [
    {
        id: 'stop-slop',
        name: 'Stop Slop — Writing Rules',
        icon: '✍️',
        category: 'writing',
        shortDescription: 'Eliminate predictable AI writing patterns from all prose your replica produces.',
        prompt: `Eliminate predictable AI writing patterns from all prose you produce.

BANNED PHRASES — Remove entirely. State the content directly instead.

Throat-clearing openers: "Here's the thing:", "The thing is,", "Here's what's interesting:", "Here's why that matters:", "Let me be clear:", "I want to be honest:", "Let's be real:", "Look,", "So,", "Now,", "Right.", "Okay.", "Certainly!", "Absolutely!", "Of course!", "Sure thing!"

Emphasis crutches: "Let that sink in.", "Full stop.", "Period.", "And that's okay.", "This matters.", "That's it. That's the secret.", "[Noun]. That's it. That's the [thing].", "It's that simple."

Vague declaratives: "This matters because", "What matters is", "The truth is", "The reality is", "The uncomfortable truth is", "The real question is", "The answer is simple."

Business jargon: "Navigate" (for challenges), "Unpack" (before analysis), "Landscape", "Ecosystem", "Lean into", "Dive deep", "Double down", "Move the needle", "At the end of the day", "It is what it is."

Meta-commentary: "I'm going to walk you through", "Let me explain", "What I mean by this is", "In other words,", "To put it simply,", "To be fair,", "To be clear,"

All adverbs: No -ly words. No softeners (slightly, somewhat, rather), no intensifiers (incredibly, extremely, deeply), no hedges (probably, arguably, essentially, ultimately, literally, simply, just, really, very, quite, actually).

BANNED STRUCTURES:
- Binary contrasts: Avoid "Not because X. Because Y." — state the positive directly.
- Negative listings: Don't list what something isn't before saying what it is.
- Dramatic fragmentation: Avoid single-word or two-word sentences for emphasis.
- Rhetorical question setups: Don't ask a question and immediately answer it.
- False agency / passive voice: Name the human actor.
- Narrator-from-a-distance voice: Don't describe what you're about to say. Say it.
- Tripling: Resist always giving exactly three examples. Two is often stronger. One can be enough.

STYLE RULES:
- Vary rhythm. Mix sentence lengths.
- Trust readers. State facts directly. Skip softening, justification, and hand-holding.
- End paragraphs differently. Not every paragraph needs a punchy one-liner closer.
- Cut quotables. If a sentence sounds like a pull-quote or Instagram caption, rewrite it.
- Em-dash discipline. Don't use an em-dash to set up a reveal or twist.

QUICK CHECKS BEFORE DELIVERING PROSE:
- Three consecutive sentences match length? Break one.
- Paragraph ends with punchy one-liner? Vary it.
- Em-dash before a reveal? Remove it.
- Explaining a metaphor? Trust it to land.
- Any -ly adverb? Delete it.
- Did you start with a greeting or opener like "Certainly!" or "Great question"? Cut it. Start with the substance.

SCORING — Before finalising any prose, rate it 1–10 on: Directness, Rhythm, Trust, Authenticity, Density. Below 35/50: revise.

Based on Stop Slop by Hardik Pandya — MIT License`,
    },
    {
        id: 'concise-expert',
        name: 'Concise Expert',
        icon: '⚡',
        category: 'tone',
        shortDescription: 'Short, precise answers. No fluff, no filler. Straight to the point.',
        prompt: `Keep responses short and precise. Answer in the fewest words needed to be accurate and complete.

Rules:
- Lead with the answer, then add context only if necessary.
- Use bullet points over paragraphs when listing.
- Skip introductions, summaries, and sign-offs.
- If the user asks a yes/no question, start with yes or no.
- Omit hedging phrases ("I think", "it seems", "you might want to").
- Prefer active voice and concrete nouns.
- Never repeat the question back to the user.`,
    },
    {
        id: 'structured-output',
        name: 'Structured Output',
        icon: '📐',
        category: 'formatting',
        shortDescription: 'Always use headings, bullets, and tables to organize information.',
        prompt: `Structure every response for scanability.

Rules:
- Use markdown headings (##, ###) to separate sections.
- Use bullet points for lists of 2+ items.
- Use tables when comparing items or showing tabular data.
- Use code blocks for any code, commands, or technical output.
- Bold key terms on first use.
- Number steps in any sequential process.
- Keep paragraphs to 3 sentences maximum.`,
    },
];

// ── Custom instruction block type ───────────────────────────────────
interface CustomInstruction {
    id: string;
    name: string;
    category: string;
    prompt: string;
    enabled: boolean;
}

// ── Persistence ─────────────────────────────────────────────────────
const INSTRUCTIONS_KEY = 'humanreplica_replica_instructions';
const BROWSER_SETTINGS_KEY = 'humanreplica_browser_settings';

interface InstructionsState {
    activeTemplateIds: string[];
    customInstructions: CustomInstruction[];
}

type BrowserMode = 'off' | 'allowed' | 'full';

interface BrowserSettingsState {
    mode: BrowserMode;
    allowedDomains: string[];
}

function loadInstructions(): InstructionsState {
    try {
        const raw = localStorage.getItem(INSTRUCTIONS_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { activeTemplateIds: [], customInstructions: [] };
}

function saveInstructions(s: InstructionsState) {
    localStorage.setItem(INSTRUCTIONS_KEY, JSON.stringify(s));
}

function loadBrowserSettings(): BrowserSettingsState {
    try {
        const raw = localStorage.getItem(BROWSER_SETTINGS_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { mode: 'off', allowedDomains: [] };
}

function saveBrowserSettings(s: BrowserSettingsState) {
    localStorage.setItem(BROWSER_SETTINGS_KEY, JSON.stringify(s));
}

const CATEGORIES = [
    { id: 'writing', label: 'Writing', icon: '✍️' },
    { id: 'tone', label: 'Tone', icon: '🎯' },
    { id: 'formatting', label: 'Formatting', icon: '📐' },
    { id: 'domain', label: 'Domain', icon: '🏢' },
    { id: 'custom', label: 'Custom', icon: '⚙️' },
];

// ── Main Component ──────────────────────────────────────────────────
export default function ReplicaAgentTab() {
    const [temperature, setTemperature] = useState(0.7);
    const [autoApprove, setAutoApprove] = useState(false);
    const [sessionTimeout, setSessionTimeout] = useState('30');
    const [personalityPreset, setPersonalityPreset] = useState('professional');
    const [streamingResponses, setStreamingResponses] = useState(true);

    // Instructions state
    const [instructions, setInstructions] = useState<InstructionsState>(loadInstructions);
    const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
    const [expandedCustom, setExpandedCustom] = useState<string | null>(null);

    // Browser control state
    const [browserSettings, setBrowserSettings] = useState<BrowserSettingsState>(loadBrowserSettings);
    const [newDomain, setNewDomain] = useState('');

    useEffect(() => { saveInstructions(instructions); }, [instructions]);
    useEffect(() => { saveBrowserSettings(browserSettings); }, [browserSettings]);

    const setBrowserMode = (mode: BrowserMode) => {
        setBrowserSettings(prev => ({ ...prev, mode }));
    };

    const addDomain = () => {
        const domain = newDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        if (domain && !browserSettings.allowedDomains.includes(domain)) {
            setBrowserSettings(prev => ({
                ...prev,
                allowedDomains: [...prev.allowedDomains, domain],
            }));
        }
        setNewDomain('');
    };

    const removeDomain = (domain: string) => {
        setBrowserSettings(prev => ({
            ...prev,
            allowedDomains: prev.allowedDomains.filter(d => d !== domain),
        }));
    };

    const isTemplateActive = (id: string) => instructions.activeTemplateIds.includes(id);

    const toggleTemplate = (id: string) => {
        setInstructions(prev => ({
            ...prev,
            activeTemplateIds: prev.activeTemplateIds.includes(id)
                ? prev.activeTemplateIds.filter(t => t !== id)
                : [...prev.activeTemplateIds, id],
        }));
    };

    const addCustomInstruction = () => {
        const newInstr: CustomInstruction = {
            id: `custom_${Date.now()}`,
            name: '',
            category: 'custom',
            prompt: '',
            enabled: true,
        };
        setInstructions(prev => ({
            ...prev,
            customInstructions: [...prev.customInstructions, newInstr],
        }));
        setExpandedCustom(newInstr.id);
    };

    const updateCustomInstruction = (id: string, update: Partial<CustomInstruction>) => {
        setInstructions(prev => ({
            ...prev,
            customInstructions: prev.customInstructions.map(c =>
                c.id === id ? { ...c, ...update } : c
            ),
        }));
    };

    const removeCustomInstruction = (id: string) => {
        setInstructions(prev => ({
            ...prev,
            customInstructions: prev.customInstructions.filter(c => c.id !== id),
        }));
    };

    const activeCount = instructions.activeTemplateIds.length
        + instructions.customInstructions.filter(c => c.enabled).length;

    return (
        <div className="space-y-6">
            {/* ── Personality ─────────────────────────────── */}
            <div className="card p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Bot size={20} className="text-hig-accent" />
                    <div>
                        <h2 className="text-lg font-semibold text-hig-text-primary">Personality & Tone</h2>
                        <p className="text-xs text-hig-text-tertiary">Default behavior for all your replicas</p>
                    </div>
                </div>

                <div className="space-y-5">
                    {/* Preset */}
                    <div>
                        <label className="block text-sm font-medium text-hig-text-secondary mb-2">Personality Preset</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                                { id: 'professional', label: 'Professional', emoji: '💼' },
                                { id: 'friendly', label: 'Friendly', emoji: '😊' },
                                { id: 'concise', label: 'Concise', emoji: '⚡' },
                                { id: 'creative', label: 'Creative', emoji: '🎨' },
                            ].map(preset => (
                                <button key={preset.id}
                                    onClick={() => setPersonalityPreset(preset.id)}
                                    className={`p-3 rounded-xl border text-center transition-all ${personalityPreset === preset.id
                                        ? 'border-hig-accent/30 bg-hig-accent/5'
                                        : 'border-hig-divider hover:bg-hig-bg-tertiary'
                                    }`}>
                                    <span className="text-lg mb-1 block">{preset.emoji}</span>
                                    <span className="text-xs font-medium text-hig-text-primary">{preset.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Temperature */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-hig-text-secondary">Creativity Level</label>
                            <span className="text-xs font-mono text-hig-text-tertiary bg-hig-bg-tertiary px-2 py-0.5 rounded">{temperature.toFixed(1)}</span>
                        </div>
                        <input
                            type="range" min="0" max="1" step="0.1"
                            value={temperature}
                            onChange={(e) => setTemperature(parseFloat(e.target.value))}
                            className="w-full h-2 bg-hig-bg-tertiary rounded-full appearance-none cursor-pointer accent-hig-accent"
                        />
                        <div className="flex justify-between text-[10px] text-hig-text-tertiary mt-1">
                            <span>Precise & Deterministic</span>
                            <span>Creative & Varied</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Replica Instructions ────────────────────── */}
            <div className="card p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <FileText size={20} className="text-hig-accent" />
                        <div>
                            <h2 className="text-lg font-semibold text-hig-text-primary">Replica Instructions</h2>
                            <p className="text-xs text-hig-text-tertiary">
                                Shape how your replica writes and responds — {activeCount} active instruction{activeCount !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Built-in Templates */}
                <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={14} className="text-amber-400" />
                        <span className="text-xs font-bold text-hig-text-tertiary uppercase tracking-widest">Built-in Templates</span>
                    </div>
                    <div className="space-y-2">
                        {BUILT_IN_TEMPLATES.map(tmpl => {
                            const active = isTemplateActive(tmpl.id);
                            const expanded = expandedTemplate === tmpl.id;
                            return (
                                <div key={tmpl.id}
                                    className={`rounded-xl border transition-all ${active
                                        ? 'border-hig-accent/30 bg-hig-accent/5'
                                        : 'border-hig-divider bg-hig-bg-secondary'
                                    }`}>
                                    <div className="flex items-center gap-3 px-4 py-3">
                                        <span className="text-xl flex-shrink-0">{tmpl.icon}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-hig-text-primary truncate">{tmpl.name}</p>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-bg-tertiary text-hig-text-tertiary capitalize flex-shrink-0">
                                                    {tmpl.category}
                                                </span>
                                            </div>
                                            <p className="text-xs text-hig-text-secondary mt-0.5 truncate">{tmpl.shortDescription}</p>
                                        </div>
                                        <button
                                            onClick={() => setExpandedTemplate(expanded ? null : tmpl.id)}
                                            className="p-1.5 rounded-lg hover:bg-hig-bg-tertiary text-hig-text-tertiary transition-colors flex-shrink-0"
                                            title={expanded ? 'Collapse' : 'Preview prompt'}
                                        >
                                            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                        </button>
                                        <Toggle on={active} onToggle={() => toggleTemplate(tmpl.id)} />
                                    </div>
                                    {expanded && (
                                        <div className="px-4 pb-4 pt-0">
                                            <div className="bg-hig-bg-tertiary rounded-lg p-3 max-h-64 overflow-y-auto">
                                                <pre className="text-xs text-hig-text-secondary whitespace-pre-wrap font-mono leading-relaxed">
                                                    {tmpl.prompt}
                                                </pre>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Custom Instructions */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <MessageCircle size={14} className="text-sky-400" />
                            <span className="text-xs font-bold text-hig-text-tertiary uppercase tracking-widest">Custom Instructions</span>
                        </div>
                        <button
                            onClick={addCustomInstruction}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-hig-accent bg-hig-accent/10 hover:bg-hig-accent/20 transition-colors"
                        >
                            <Plus size={12} />
                            Add Instruction
                        </button>
                    </div>

                    {instructions.customInstructions.length === 0 ? (
                        <div className="text-center py-8 rounded-xl border border-dashed border-hig-divider">
                            <FileText size={24} className="mx-auto text-hig-text-tertiary mb-2 opacity-40" />
                            <p className="text-xs text-hig-text-tertiary">No custom instructions yet.</p>
                            <p className="text-xs text-hig-text-tertiary mt-0.5">Add one to shape your replica's behavior.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {instructions.customInstructions.map(instr => {
                                const expanded = expandedCustom === instr.id;
                                return (
                                    <div key={instr.id}
                                        className={`rounded-xl border transition-all ${instr.enabled
                                            ? 'border-hig-accent/20 bg-hig-accent/[0.03]'
                                            : 'border-hig-divider bg-hig-bg-secondary opacity-60'
                                        }`}>
                                        <div className="flex items-center gap-3 px-4 py-3">
                                            <span className="text-lg flex-shrink-0">
                                                {CATEGORIES.find(c => c.id === instr.category)?.icon || '⚙️'}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-hig-text-primary truncate">
                                                    {instr.name || 'Untitled Instruction'}
                                                </p>
                                                <p className="text-[10px] text-hig-text-tertiary capitalize">{instr.category}</p>
                                            </div>
                                            <button
                                                onClick={() => setExpandedCustom(expanded ? null : instr.id)}
                                                className="p-1.5 rounded-lg hover:bg-hig-bg-tertiary text-hig-text-tertiary transition-colors flex-shrink-0"
                                            >
                                                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                            <Toggle on={instr.enabled} onToggle={() => updateCustomInstruction(instr.id, { enabled: !instr.enabled })} />
                                            <button
                                                onClick={() => removeCustomInstruction(instr.id)}
                                                className="p-1.5 rounded-lg hover:bg-red-500/10 text-hig-text-tertiary hover:text-red-400 transition-colors flex-shrink-0"
                                                title="Delete instruction"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                        {expanded && (
                                            <div className="px-4 pb-4 pt-0 space-y-3">
                                                <div className="flex gap-3">
                                                    <div className="flex-1">
                                                        <label className="text-[10px] text-hig-text-tertiary font-medium uppercase tracking-wider block mb-1">Name</label>
                                                        <input
                                                            value={instr.name}
                                                            onChange={e => updateCustomInstruction(instr.id, { name: e.target.value })}
                                                            placeholder="e.g. My Writing Style"
                                                            className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:border-hig-accent"
                                                        />
                                                    </div>
                                                    <div className="w-36">
                                                        <label className="text-[10px] text-hig-text-tertiary font-medium uppercase tracking-wider block mb-1">Category</label>
                                                        <select
                                                            value={instr.category}
                                                            onChange={e => updateCustomInstruction(instr.id, { category: e.target.value })}
                                                            className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent appearance-none cursor-pointer"
                                                        >
                                                            {CATEGORIES.map(cat => (
                                                                <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-hig-text-tertiary font-medium uppercase tracking-wider block mb-1">Prompt</label>
                                                    <textarea
                                                        value={instr.prompt}
                                                        onChange={e => updateCustomInstruction(instr.id, { prompt: e.target.value.slice(0, 4000) })}
                                                        rows={6}
                                                        placeholder="Write the instruction prompt your replica should follow…"
                                                        className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:border-hig-accent resize-none font-mono leading-relaxed"
                                                    />
                                                    <p className="text-[10px] text-hig-text-tertiary mt-1 text-right">{instr.prompt.length}/4000</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Browser Control ──────────────────────────── */}
            <div className="card p-6">
                <div className="flex items-center gap-3 mb-2">
                    <Globe size={20} className="text-hig-accent" />
                    <div className="flex-1">
                        <h2 className="text-lg font-semibold text-hig-text-primary">Browser Control</h2>
                        <p className="text-xs text-hig-text-tertiary">Allow your replica to navigate and interact with web pages</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                        browserSettings.mode === 'off'
                            ? 'bg-hig-bg-tertiary text-hig-text-tertiary'
                            : browserSettings.mode === 'allowed'
                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    }`}>
                        {browserSettings.mode === 'off' ? 'Disabled' : browserSettings.mode === 'allowed' ? 'Restricted' : 'Full Access'}
                    </span>
                </div>

                {/* 3-way mode selector */}
                <div className="grid grid-cols-3 gap-2 mt-5 mb-5">
                    {[
                        { id: 'off' as BrowserMode, label: 'Off', emoji: '🚫', desc: 'No browser access' },
                        { id: 'allowed' as BrowserMode, label: 'Allowed Domains', emoji: '🔒', desc: 'Only whitelisted sites' },
                        { id: 'full' as BrowserMode, label: 'Full Access', emoji: '🌐', desc: 'Any website' },
                    ].map(opt => (
                        <button key={opt.id}
                            onClick={() => setBrowserMode(opt.id)}
                            className={`p-3 rounded-xl border text-center transition-all ${
                                browserSettings.mode === opt.id
                                    ? opt.id === 'off'
                                        ? 'border-hig-text-tertiary/30 bg-hig-bg-tertiary'
                                        : opt.id === 'allowed'
                                        ? 'border-amber-500/30 bg-amber-500/5'
                                        : 'border-emerald-500/30 bg-emerald-500/5'
                                    : 'border-hig-divider hover:bg-hig-bg-tertiary'
                            }`}
                        >
                            <span className="text-lg mb-1 block">{opt.emoji}</span>
                            <span className="text-xs font-semibold text-hig-text-primary block">{opt.label}</span>
                            <span className="text-[10px] text-hig-text-tertiary block mt-0.5">{opt.desc}</span>
                        </button>
                    ))}
                </div>

                {/* Domain Manager — only visible when mode is 'allowed' */}
                {browserSettings.mode === 'allowed' && (
                    <div className="mt-1 pt-5 border-t border-hig-divider">
                        <label className="text-xs font-bold text-hig-text-tertiary uppercase tracking-widest block mb-3">Allowed Domains</label>

                        {/* Add domain input */}
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                value={newDomain}
                                onChange={e => setNewDomain(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                                placeholder="e.g. google.com"
                                className="flex-1 px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:border-hig-accent"
                            />
                            <button
                                onClick={addDomain}
                                disabled={!newDomain.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-hig-accent bg-hig-accent/10 hover:bg-hig-accent/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <Plus size={12} />
                                Add
                            </button>
                        </div>

                        {/* Domain tags */}
                        {browserSettings.allowedDomains.length === 0 ? (
                            <div className="text-center py-6 rounded-xl border border-dashed border-hig-divider">
                                <Globe size={20} className="mx-auto text-hig-text-tertiary mb-1.5 opacity-40" />
                                <p className="text-xs text-hig-text-tertiary">No domains added yet.</p>
                                <p className="text-xs text-hig-text-tertiary mt-0.5">Your replica won't be able to browse until you add allowed domains.</p>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {browserSettings.allowedDomains.map(domain => (
                                    <span key={domain}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-hig-bg-tertiary border border-hig-divider text-xs text-hig-text-primary font-medium group hover:border-red-400/30 transition-colors"
                                    >
                                        <Globe size={11} className="text-hig-text-tertiary" />
                                        {domain}
                                        <button
                                            onClick={() => removeDomain(domain)}
                                            className="p-0.5 rounded-full text-hig-text-tertiary hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                            title={`Remove ${domain}`}
                                        >
                                            <X size={11} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Full access warning */}
                {browserSettings.mode === 'full' && (
                    <div className="mt-1 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                        <p className="text-xs text-amber-300 leading-relaxed">
                            <strong>⚠️ Full Access</strong> — Your replica can navigate to any website. Write actions (clicking, typing) still require your approval before execution.
                        </p>
                    </div>
                )}
            </div>

            {/* ── Behavior ────────────────────────────────── */}
            <div className="card p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Sliders size={20} className="text-hig-accent" />
                    <h2 className="text-lg font-semibold text-hig-text-primary">Behavior</h2>
                </div>

                <div className="space-y-1">
                    <div className="flex items-center justify-between py-3 border-b border-hig-divider">
                        <div className="flex items-start gap-3">
                            <ShieldCheck size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-medium text-hig-text-primary">Auto-Approve Low-Risk Actions</p>
                                <p className="text-sm text-hig-text-secondary">Skip approval prompts for read-only and informational tasks</p>
                            </div>
                        </div>
                        <Toggle on={autoApprove} onToggle={() => setAutoApprove(!autoApprove)} />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-hig-divider">
                        <div className="flex items-start gap-3">
                            <MessageCircle size={16} className="text-sky-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-medium text-hig-text-primary">Streaming Responses</p>
                                <p className="text-sm text-hig-text-secondary">Show responses as they are generated (token by token)</p>
                            </div>
                        </div>
                        <Toggle on={streamingResponses} onToggle={() => setStreamingResponses(!streamingResponses)} />
                    </div>

                    <div className="flex items-center justify-between py-3">
                        <div className="flex items-start gap-3">
                            <Clock size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-medium text-hig-text-primary">Session Timeout</p>
                                <p className="text-sm text-hig-text-secondary">Auto-pause replica after inactivity</p>
                            </div>
                        </div>
                        <select value={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.value)}
                            className="px-3 py-1.5 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent appearance-none cursor-pointer">
                            <option value="15">15 min</option>
                            <option value="30">30 min</option>
                            <option value="60">1 hour</option>
                            <option value="120">2 hours</option>
                            <option value="0">Never</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}
